import React, { useState, useEffect } from 'react';
import { meetingTemplatesApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Pencil, Trash2, FileText, Clock, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

const MEETING_TYPES = ["Daily", "Weekly", "15-Day", "30-45 Day Strategic"];

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await meetingTemplatesApi.getAll();
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await meetingTemplatesApi.delete(id);
      setTemplates(templates.filter(t => t.id !== id));
      toast.success('Template deleted');
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 skeleton rounded-lg" />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-center justify-between">
        <div className="page-header mb-0">
          <h1 className="page-title">Meeting Templates</h1>
          <p className="page-subtitle">Define agenda structures for different meeting types</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditingTemplate(null)} data-testid="create-template-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Template
            </Button>
          </DialogTrigger>
          <TemplateDialog
            template={editingTemplate}
            onClose={() => setDialogOpen(false)}
            onSave={loadTemplates}
          />
        </Dialog>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {templates.map(template => (
          <Card key={template.id} className="border border-slate-200">
            <CardHeader className="flex flex-row items-start justify-between pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">{template.meeting_type}</CardTitle>
                  <p className="text-sm text-slate-500">{template.rows?.length || 0} agenda items</p>
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { setEditingTemplate(template); setDialogOpen(true); }}
                  data-testid={`edit-template-${template.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(template.id)}
                  data-testid={`delete-template-${template.id}`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {template.rows?.slice(0, 4).map((row, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium w-16">{row.time_duration}</span>
                    <span className="text-sm text-slate-600 flex-1">{row.agenda}</span>
                  </div>
                ))}
                {template.rows?.length > 4 && (
                  <p className="text-xs text-slate-500 text-center">+{template.rows.length - 4} more items</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {templates.length === 0 && (
          <Card className="col-span-2 border border-dashed border-slate-300">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold mb-2">No templates yet</h3>
              <p className="text-slate-500 mb-4">Create a template to define meeting agendas</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function TemplateDialog({ template, onClose, onSave }) {
  const [form, setForm] = useState({
    meeting_type: 'Daily',
    rows: [{ time_duration: '5 min', agenda: '', facilitator: '', highlights: '', actionables: '' }]
  });
  const [loading, setLoading] = useState(false);

  // Initialize form when template changes (for edit mode)
  useEffect(() => {
    if (template) {
      setForm({
        meeting_type: template.meeting_type || 'Daily',
        rows: template.rows?.length > 0 
          ? template.rows.map(r => ({ ...r })) 
          : [{ time_duration: '5 min', agenda: '', facilitator: '', highlights: '', actionables: '' }]
      });
    } else {
      // Reset to defaults for create mode
      setForm({
        meeting_type: 'Daily',
        rows: [{ time_duration: '5 min', agenda: '', facilitator: '', highlights: '', actionables: '' }]
      });
    }
  }, [template?.id]);

  const addRow = () => {
    setForm(prev => ({
      ...prev,
      rows: [...prev.rows, { time_duration: '5 min', agenda: '', facilitator: '', highlights: '', actionables: '' }]
    }));
  };

  const updateRow = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      rows: prev.rows.map((row, i) => i === index ? { ...row, [field]: value } : row)
    }));
  };

  const removeRow = (index) => {
    setForm(prev => ({
      ...prev,
      rows: prev.rows.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.rows.length === 0) {
      toast.error('Add at least one agenda item');
      return;
    }
    setLoading(true);
    try {
      if (template) {
        await meetingTemplatesApi.update(template.id, form);
        toast.success('Template updated');
      } else {
        await meetingTemplatesApi.create(form);
        toast.success('Template created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{template ? 'Edit Template' : 'Create Template'}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Meeting Type</Label>
          <Select value={form.meeting_type} onValueChange={(v) => setForm({...form, meeting_type: v})}>
            <SelectTrigger data-testid="template-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MEETING_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Agenda Items</Label>
            <Button type="button" variant="outline" size="sm" onClick={addRow}>
              <Plus className="w-3 h-3 mr-1" /> Add Row
            </Button>
          </div>
          
          <div className="space-y-3">
            {form.rows.map((row, idx) => (
              <div key={idx} className="p-4 bg-slate-50 rounded-lg border">
                <div className="flex items-start gap-3">
                  <GripVertical className="w-4 h-4 text-slate-400 mt-3" />
                  <div className="flex-1 space-y-3">
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs">Duration</Label>
                        <Input
                          value={row.time_duration}
                          onChange={(e) => updateRow(idx, 'time_duration', e.target.value)}
                          placeholder="5 min"
                          className="mt-1"
                          data-testid={`row-duration-${idx}`}
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">Agenda / Points</Label>
                        <Input
                          value={row.agenda}
                          onChange={(e) => updateRow(idx, 'agenda', e.target.value)}
                          placeholder="Agenda item description"
                          className="mt-1"
                          data-testid={`row-agenda-${idx}`}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Facilitator</Label>
                        <Input
                          value={row.facilitator}
                          onChange={(e) => updateRow(idx, 'facilitator', e.target.value)}
                          placeholder="Who leads"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Key Highlights</Label>
                        <Input
                          value={row.highlights}
                          onChange={(e) => updateRow(idx, 'highlights', e.target.value)}
                          placeholder="Points to highlight"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Actionables</Label>
                        <Input
                          value={row.actionables}
                          onChange={(e) => updateRow(idx, 'actionables', e.target.value)}
                          placeholder="Action steps"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(idx)}
                    disabled={form.rows.length === 1}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={loading} data-testid="save-template-btn">
            {loading ? 'Saving...' : 'Save Template'}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
}
